import React, { useEffect } from "react";
import { Suspense } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import { pingTask } from "../../../../util/apiService";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { SharedHistoryContext } from "../../../../common/RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../../../common/RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../../../common/RichTextEditor/LexicalEditor/context/ToolbarContext";
import Editor from "../../../../common/RichTextEditor/Editor/Editor";
import { buildImportMap } from "../../../../util/lexical";
import PlaygroundNodes from "../../../../common/RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import {
  NODE_SELECTION_BLUE,
  isDescendantLockedPreview,
  isAuditPreviewFocusedLeafCheckbox,
  getDescendantLockedCheckboxStyle,
  getHaloWrapperStyle,
  getAuditPreviewReadonlyHaloCheckboxStyle,
  auditPreviewSectionDescendantWrapStyle,
} from "./nodeSelectionCheckboxStyles";

const VideoNode = ({
  node,
  nodeNumber,
  parentContext,
  nodeType,
  onNavigate,
  checkboxState,
  disabled = false,
  showCheckbox = true,
  showHeading = null,
  checkboxLabelColor = null,
  isRevisionLocked = false,
}) => {
  const authUser = useSelector((state) => state.auth.user);
  const nodeHeading = `Video ${node.is_deleted ? "(Deleted)" : nodeNumber}`;
  const breadcrumbStepNumber =
    parentContext?.globalStepNumber != null
      ? parentContext.globalStepNumber
      : typeof parentContext?.stepIndex === "number"
        ? parentContext.stepIndex + 1
        : "";

  // Get revisionId and authUser for ping API
  const location = useLocation();
  const params = useParams();

  // Extract revisionId from params (could be revisionId or revision_id depending on route)
  const revisionId = params.revisionId || params.revision_id;

  // Check if we're on a view-revision route
  const isViewRevisionRoute =
    location.pathname.includes("/view-revision") ||
    location.pathname.includes("/view-section") ||
    location.pathname.includes("/view-step") ||
    location.pathname.includes("/view-substep") ||
    location.pathname.includes("/view-childnode");

  // Start ping interval if on view-revision route
  useEffect(() => {
    if (!isViewRevisionRoute || !revisionId || !authUser) return;

    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revisionId,
          user_id: authUser.id,
        };
        await pingTask(pingData);
      } catch (error) {
        console.error("Ping failed in VideoNode:", error);
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(interval);
    };
  }, [isViewRevisionRoute, revisionId, authUser]);

  // Determine checkbox state for this node
  // Audit `preview_tree_node_id` may be procedure tree id or `video_tree_node_id` — match like CanvasNode.
  const videoIdMatches = (id) =>
    id != null &&
    (id === node.tree_node_id ||
      id === node.detail?.video_tree_node_id ||
      id === node.detail?.procedure_tree_ref);
  const isCurrent =
    checkboxState?.currentNodeId != null &&
    videoIdMatches(checkboxState.currentNodeId) &&
    (checkboxState?.currentNodeType === "video" ||
      checkboxState?.currentNodeType === nodeType);
  const isSourceVideo =
    checkboxState?.sourceNodeId != null &&
    videoIdMatches(checkboxState.sourceNodeId);
  const isChecked =
    isCurrent || checkboxState?.allChildrenChecked || isSourceVideo;
  const isDisabled =
    disabled ||
    (checkboxState?.allChildrenDisabled && !isCurrent) ||
    (isRevisionLocked &&
      isRevisionLocked.lock &&
      isRevisionLocked.lock.user_id !== authUser?.id);
  const isDescendantLocked = isDescendantLockedPreview(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceVideo,
  );
  const isAuditPreviewFocusedLeaf = isAuditPreviewFocusedLeafCheckbox(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceVideo,
  );
  const isPreviewLockedLeafStyle = isDescendantLocked || isAuditPreviewFocusedLeaf;

  return (
    <div
      key={node.tree_node_id}
      data-export-block="video"
      style={{ marginTop: showCheckbox ? "20px" : "30px", clear: "both" }}
    >
      {/* Show heading - use showHeading if provided, otherwise fall back to showCheckbox */}
      {(showHeading !== null ? showHeading : showCheckbox) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            {showCheckbox && (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  ...auditPreviewSectionDescendantWrapStyle(checkboxState),
                }}
              >
                {isDescendantLocked ? (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    onChange={() => {}}
                    style={getDescendantLockedCheckboxStyle()}
                  />
                ) : isAuditPreviewFocusedLeaf ? (
                  <div style={getHaloWrapperStyle()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      tabIndex={-1}
                      aria-readonly="true"
                      onChange={() => {}}
                      style={getAuditPreviewReadonlyHaloCheckboxStyle()}
                    />
                  </div>
                ) : (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => {
                    // Prevent action for source video being moved
                    if (isSourceVideo) {
                      return;
                    }
                    if (!isDisabled && onNavigate) {
                      onNavigate(nodeType || "video", node, {
                        ...parentContext,
                        nodeNumber,
                      });
                    }
                  }}
                  style={{
                    cursor: isDisabled
                      ? "not-allowed"
                      : isSourceVideo
                        ? "not-allowed"
                        : "pointer",
                    accentColor: isSourceVideo
                      ? "rgb(68, 68, 68)"
                      : isChecked
                        ? NODE_SELECTION_BLUE
                        : undefined,
                    backgroundColor: isSourceVideo
                      ? "rgb(68, 68, 68)"
                      : undefined,
                  }}
                />
                )}
                {/* Red cross icon overlay - shown when disabled and hovering */}
                {isDisabled && !isPreviewLockedLeafStyle && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = "0";
                    }}
                    className="checkbox-hover-overlay"
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      size="sm"
                      color="red"
                      style={{
                        filter: "drop-shadow(0 0 1px rgba(255,255,255,0.8))",
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            <h2
              onClick={() => {
                // Prevent action for source video being moved
                if (isSourceVideo) {
                  return;
                }
                if (isPreviewLockedLeafStyle) return;
                if (!isDisabled && showCheckbox && onNavigate) {
                  onNavigate(nodeType || "video", node, {
                    ...parentContext,
                    nodeNumber,
                  });
                }
              }}
              style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: isPreviewLockedLeafStyle ? 700 : undefined,
                cursor: isSourceVideo
                  ? "not-allowed"
                  : isPreviewLockedLeafStyle
                    ? "default"
                  : !isDisabled && showCheckbox
                    ? "pointer"
                    : "default",
                color: isPreviewLockedLeafStyle
                  ? "#111"
                  : showCheckbox
                    ? checkboxLabelColor || undefined
                    : undefined,
                userSelect: "none",
              }}
            >
              {nodeHeading}
            </h2>
          </div>

          <div
            style={{
              color: "#888",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            {!node.is_deleted &&
              (parentContext?.auditSubstepBreadcrumbFull ? (
                <>{parentContext.auditSubstepBreadcrumbFull}</>
              ) : (
                <>
                  Section {parentContext.sectionIndex + 1} &gt; Step{" "}
                  {breadcrumbStepNumber}
                  {parentContext.substepLetter && (
                    <>
                      {" "}
                      &gt; Substep {breadcrumbStepNumber}
                      {parentContext.substepLetter}
                    </>
                  )}
                </>
              ))}
          </div>
        </div>
      )}

      {/* Video Preview - using video_url for HTTPS URL */}
      {node.detail?.video && (
        <video
          controls
          data-video-poster={
            node.detail?.poster ||
            node.detail?.poster_url ||
            node.detail?.thumbnail ||
            node.detail?.thumbnail_url ||
            node.detail?.video_thumbnail ||
            node.detail?.video_thumbnail_url ||
            ""
          }
          style={{ width: "100%", marginTop: "10px", height: "400px" }}
          src={node.detail.video}
        >
          Your browser does not support the video tag.
        </video>
      )}

      {/* Display video_url if available */}
      {(node.detail?.video_url ||
        (node.detail?.videos && node.detail.videos[0]?.video_url)) && (
        <div style={{ marginTop: "10px" }}>
          {node.detail?.video_url ||
            (node.detail?.videos && node.detail.videos[0]?.video_url)}
        </div>
      )}

      {(() => {
                      const rawShort = node.detail?.short_description || "";
                      // Convert plain text with quotes to HTML format for proper rendering
                      const contentHtml =
                        typeof rawShort === "string" && rawShort.includes('class="editor-shell"')
                          ? rawShort.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1")
                          : rawShort && rawShort.trim() !== ""
                            ? rawShort.includes('<') && rawShort.includes('>')
                              ? rawShort // Already HTML, use as-is
                              : `<p>${rawShort.replace(/"/g, '&quot;')}</p>` // Plain text, wrap in p tags
                            : rawShort;
                      return contentHtml ? (
                        <div style={{ marginTop: "5px" }}>
                          <div className="rich-text-content-display">
                            <Suspense fallback={<div>Loading...</div>}>
                              <LexicalComposer
                                initialConfig={{
                                  editorState: null,
                                  html: { import: buildImportMap() },
                                  namespace: "ShortDescriptionViewer",
                                  nodes: [...PlaygroundNodes],
                                  onError: (error) => {
                                    console.error("Error in Short Description viewer:", error);
                                  },
                                  theme: PlaygroundEditorTheme,
                                  readOnly: true,
                                  editable: false,
                                }}
                              >
                                <SharedHistoryContext>
                                  <TableContext>
                                    <ToolbarContext>
                                      <div className="editor-shell">
                                        <Editor
                                          handleEditorChange={() => {}}
                                          initialContent={contentHtml}
                                        />
                                      </div>
                                    </ToolbarContext>
                                  </TableContext>
                                </SharedHistoryContext>
                              </LexicalComposer>
                            </Suspense>
                          </div>
                        </div>
                      ) : null;
                    })()}
    </div>
  );
};

export default VideoNode;
